import React, { useState } from "react";
import styled from "styled-components/macro";
import { COLORS, FONTSIZE, FONTWEIGHT, CONSTANTS } from "helpers/designConstants";
import { Button } from "components/shared/standardElements";
import LoginContentWrapper from "components/shared/loginContentWrapper/LoginContentWrapper";

const StoreCodeEntryForm = ({ onSubmitCode, isLoading }) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");

  const sanitizeInput = (value) => {
    return value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 6);
  };

  const handleInputChange = (e) => {
    setCode(sanitizeInput(e.target.value));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const sanitized = code.trim().toUpperCase();
    if (!sanitized) return;

    const result = await onSubmitCode(sanitized);
    if (!result) {
      setError("Invalid store code. Please try again.");
    }
  };

  return (
    <PageContainer>
      <LoginContentWrapper title="">
        <FormWrapper onSubmit={handleSubmit}>
          <Title>Enter your store code</Title>
          <Subtitle>
            Enter the code provided in your email to access the store
          </Subtitle>
          <CodeInput
            type="text"
            value={code}
            onChange={handleInputChange}
            maxLength={6}
            placeholder="CODE"
            disabled={isLoading}
            autoFocus
          />
          <SubmitButton
            type="submit"
            disabled={!code.trim() || isLoading}
          >
            {isLoading ? "Loading..." : "Go"}
          </SubmitButton>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <HelpText>
            Don't have a code? Contact the person who sent you the link.
          </HelpText>
        </FormWrapper>
      </LoginContentWrapper>
    </PageContainer>
  );
};

export default StoreCodeEntryForm;

const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
`;

const Title = styled.div`
  font-size: ${FONTSIZE.medium};
  font-weight: ${FONTWEIGHT.heavy};
  color: ${COLORS.text};
  text-transform: uppercase;
`;

const Subtitle = styled.div`
  font-size: ${FONTSIZE.small};
  color: ${COLORS.text};
  text-align: center;
  margin-bottom: 10px;
`;

const CodeInput = styled.input`
  font-size: ${FONTSIZE.xlarge};
  font-family: monospace;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 4px;
  padding: 10px 20px;
  border: 2px solid ${COLORS.secondary};
  border-radius: ${CONSTANTS.borderRadius};
  width: 220px;
  outline: none;
  color: ${COLORS.text};

  &::placeholder {
    color: ${COLORS.secondary};
  }

  &:focus {
    border-color: ${COLORS.text};
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const SubmitButton = styled(Button)`
  min-width: 100px;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
`;

const ErrorMessage = styled.div`
  font-size: ${FONTSIZE.small};
  color: ${COLORS.alertRed};
  text-align: center;
  margin-top: 5px;
`;

const HelpText = styled.div`
  font-size: ${FONTSIZE.xsmall};
  color: ${COLORS.secondary};
  text-align: center;
  margin-top: 20px;
`;
